import { Heart, Shield, Star, Clock, AlertTriangle, Briefcase, Award, UserCheck, TrendingUp, ThumbsUp, Meh, ThumbsDown } from 'lucide-react';

export const NPS_CATEGORIES = {
  promoter: { label: 'Promoter', color: '#10b981', icon: ThumbsUp, range: '9-10' },
  passive: { label: 'Passive', color: '#f59e0b', icon: Meh, range: '7-8' },
  detractor: { label: 'Detractor', color: '#ef4444', icon: ThumbsDown, range: '0-6' }
};

export const EXPERIENCE_INDICATORS = {
  'Responsive & Engaged': {
    color: '#10b981',
    icon: Heart,
    description: 'Quick response times with proactive communication',
    evidence: ['High email volume with positive NPS', 'Regular meeting cadence', 'Strong compliance scores']
  },
  'Efficient & Professional': {
    color: '#3b82f6',
    icon: Shield,
    description: 'Streamlined processes with minimal friction',
    evidence: ['Low email volume with high satisfaction', 'Excellent compliance', 'Balanced workload']
  },
  'High-Touch Service': {
    color: '#8b5cf6',
    icon: Star,
    description: 'Premium attention and personalized care',
    evidence: ['Above average meeting frequency', 'High revenue per group', 'Strong retention indicators']
  },
  'Under Pressure': {
    color: '#f59e0b',
    icon: Clock,
    description: 'Managing heavy workload that may impact service',
    evidence: ['High email backlog', 'Variable compliance scores', 'Meeting saturation']
  },
  'Needs Attention': {
    color: '#ef4444',
    icon: AlertTriangle,
    description: 'Service quality indicators suggest intervention needed',
    evidence: ['Low NPS scores', 'Compliance issues', 'Client cohort concerns']
  }
};

export const MANAGER_ARCHETYPES = {
  'Client Champion': {
    color: '#10b981',
    icon: Heart,
    description: 'Exceptional client satisfaction with strong NPS scores and positive feedback',
    criteria: 'High NPS (8+) + Strong client retention + Positive gross margin',
    recommendation: 'Model for client relationship training. Share best practices across team.',
    experienceStyle: 'Responsive & Engaged'
  },
  'Portfolio Leader': {
    color: '#3b82f6',
    icon: Briefcase,
    description: 'Manages a large portfolio with consistent performance across metrics',
    criteria: 'High group count + Above average gross margin + Acceptable NPS',
    recommendation: 'Consider for mentorship programs and scaling best practices.',
    experienceStyle: 'Efficient & Professional'
  },
  'High Performer': {
    color: '#8b5cf6',
    icon: Award,
    description: 'Achieves exceptional gross margins while maintaining service quality',
    criteria: 'Top 25% gross margin + Positive gross profit + Good NPS',
    recommendation: 'Study their efficiency methods. Consider for premium/complex clients.',
    experienceStyle: 'Efficient & Professional'
  },
  'Service Excellence': {
    color: '#06b6d4',
    icon: Star,
    description: 'Delivers premium high-touch service with strong client relationships',
    criteria: 'High NPS + Meeting-heavy + Premium service cohorts',
    recommendation: 'Ideal for complex or high-value properties requiring personal attention.',
    experienceStyle: 'High-Touch Service'
  },
  'Balanced Operator': {
    color: '#64748b',
    icon: UserCheck,
    description: 'Well-rounded performance across all metrics with steady service delivery',
    criteria: 'All metrics within normal range + Acceptable NPS',
    recommendation: 'Reliable steady performer. Good candidate for varied portfolios.',
    experienceStyle: 'Efficient & Professional'
  },
  'Growth Opportunity': {
    color: '#f97316',
    icon: TrendingUp,
    description: 'Smaller portfolio with strong service potential for expansion',
    criteria: 'Below average group count + Positive margin + Good NPS potential',
    recommendation: 'Ready for portfolio expansion or to support overloaded colleagues.',
    experienceStyle: 'Responsive & Engaged'
  },
  'Needs Support': {
    color: '#ef4444',
    icon: AlertTriangle,
    description: 'Facing challenges with profitability or client satisfaction',
    criteria: 'Low NPS or negative margin or high at-risk ratio',
    recommendation: 'Provide coaching, redistribute difficult clients, or review processes.',
    experienceStyle: 'Needs Attention'
  }
};

export function generateEvidence(mgr, avgNPS, benchmarks, atRiskRatio, profitableRatio) {
  const evidence = [];
  const insights = [];

  if (avgNPS !== null) {
    if (avgNPS >= 8.5) {
      evidence.push({ type: 'positive', text: `Strong NPS of ${avgNPS.toFixed(1)} indicates high client satisfaction` });
      insights.push('Clients are likely to recommend their services');
    } else if (avgNPS >= 7) {
      evidence.push({ type: 'neutral', text: `Average NPS of ${avgNPS.toFixed(1)} shows acceptable satisfaction` });
    } else {
      evidence.push({ type: 'negative', text: `Low NPS of ${avgNPS.toFixed(1)} suggests service issues` });
      insights.push('Review recent client interactions for improvement areas');
    }
  }

  const compliance = mgr.avgCompliance || 0;
  if (compliance >= 95) {
    evidence.push({ type: 'positive', text: `Excellent compliance score of ${compliance.toFixed(0)}%` });
  } else if (compliance < 80) {
    evidence.push({ type: 'negative', text: `Compliance score of ${compliance.toFixed(0)}% needs attention` });
  }

  if (profitableRatio >= 0.7) {
    evidence.push({ type: 'positive', text: `${(profitableRatio * 100).toFixed(0)}% of clients in positive cohorts` });
  } else if (atRiskRatio >= 0.3) {
    evidence.push({ type: 'negative', text: `${(atRiskRatio * 100).toFixed(0)}% of clients at risk or needing attention` });
  }

  const messagesPerGroup = (mgr.totalMessages || 0) / Math.max(mgr.planCount || mgr.groupCount || 1, 1);
  if (messagesPerGroup > benchmarks.avgMessages / benchmarks.avgGroupCount * 1.5) {
    evidence.push({ type: 'warning', text: `High communication volume (${messagesPerGroup.toFixed(0)} emails/group)` });
    insights.push('May benefit from streamlined communication processes');
  }

  const meetingsPerGroup = (mgr.totalMeetings || 0) / Math.max(mgr.planCount || mgr.groupCount || 1, 1);
  if (meetingsPerGroup > 2) {
    evidence.push({ type: 'neutral', text: `High-touch service model (${meetingsPerGroup.toFixed(1)} meetings/group)` });
  }

  return { evidence, insights };
}
